import React, { useCallback, useState } from "react";

import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Drawer } from "components/Measure-data/Drawer/Drawer";
import { GroupsForm } from "components/Measure-data/Groups-form/GroupsForm";
import { Group, MeasureData, MeasureDataGroup, MeasureDataRound } from "components/Measure-data/Groups-form/GroupsFormContext";
import { useMeasureDrawer } from "hooks/debt-hooks/use-measure-drawer";
import { useModals } from "hooks";
import { useDealActions } from "store";
import { useDealDefaultCurrencySelect, useDealSubAssetsSelector } from "store/selectors/deal-selectors/deal-selectors";
import { MeasureDataDraft } from "store/types";
import { IDealSecurityHistoryRow } from "store/types/deal/securities";

const HeaderCell = styled(TableCell)({
  fontSize: "14px",
  fontWeight: 500,
  color: "#071F35",
  whiteSpace: "nowrap",
  backgroundColor: "rgba(246, 248, 252, 1)",
  borderBottom: "1px solid #DBE8F5",
  padding: "6px 8px"
});

const BodyCell = styled(TableCell)({
  fontSize: "14px",
  color: "#071F35",
  borderBottom: "1px solid #EDF2F7",
  whiteSpace: "nowrap",
  padding: "6px 8px"
});

const TYPE_LABELS: Record<string, string> = {
  nav: "NAV",
  transaction: "Transaction",
  other: "Transaction"
};

interface RowProps {
  row: IDealSecurityHistoryRow;
  depth: number;
  onEdit: (row: IDealSecurityHistoryRow) => void;
  onDelete: (row: IDealSecurityHistoryRow) => void;
}

const SecuritiesHistoryRow: React.FC<RowProps> = ({ row, depth, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = row.children.length > 0;
  const leftPadding = depth * 16;
  const hasActions = !!(row.batch_id && (row.measure_ids || hasChildren));

  return (
    <>
      <TableRow
        hover
        sx={{
          "& td": { fontWeight: !depth ? 500 : 400 },
          "& .row-actions": { opacity: 0, transition: "opacity 0.2s" },
          "&:hover .row-actions": { opacity: 1 }
        }}
      >
        <BodyCell>
          <Stack direction="row" alignItems="center" sx={{ pl: `${leftPadding}px` }}>
            {hasChildren ? (
              <Box
                component="span"
                onClick={() => setExpanded((v) => !v)}
                sx={{ cursor: "pointer", display: "flex", mr: 0.5 }}
              >
                {expanded ? (
                  <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                ) : (
                  <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
                )}
              </Box>
            ) : (
              <Box sx={{ width: 22, mr: 0.5 }} />
            )}
            {row.date}
          </Stack>
        </BodyCell>
        <BodyCell>{row.round ?? "-"}</BodyCell>
        <BodyCell>{TYPE_LABELS[row.type] ?? row.type}</BodyCell>
        <BodyCell>{row.investor_name ?? "-"}</BodyCell>
        <BodyCell>{row.institution_name ?? "-"}</BodyCell>
        <BodyCell>{row.account_number ?? "-"}</BodyCell>
        <BodyCell>{row.branch_number ?? "-"}</BodyCell>
        <BodyCell>{row.units}</BodyCell>
        <BodyCell>{row.unit_price ?? "-"}</BodyCell>
        <BodyCell>{row.total}</BodyCell>
        <BodyCell sx={{ p: 0 }}>
          {hasActions && (
            <Stack className="row-actions" direction="row">
              <IconButton onClick={() => onEdit(row)}>
                <CreateOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton onClick={() => onDelete(row)}>
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          )}
        </BodyCell>
      </TableRow>
      {hasChildren &&
        expanded &&
        row.children.map((child, idx) => (
          <SecuritiesHistoryRow key={idx} row={child} depth={depth + 1} onEdit={onEdit} onDelete={onDelete} />
        ))}
    </>
  );
};

interface EditState {
  groups: Group[];
  measureData: MeasureData[];
  batchId: number;
}

interface DealSecuritiesProps {
  dealId: number | undefined;
}

export const DealSecurities: React.FC<DealSecuritiesProps> = ({ dealId }) => {
  const { getDealSecuritiesHistoryAction, createManyMeasureDataAction, saveMeasureDataDraftAction, deleteMeasureDataAction } = useDealActions();
  const { deleteModal } = useModals();
  const queryClient = useQueryClient();
  const currency = useDealDefaultCurrencySelect() || "USD";
  const subDeals = useDealSubAssetsSelector();

  const { rounds } = useMeasureDrawer(dealId ? [dealId] : []);

  const [editState, setEditState] = useState<EditState | null>(null);

  const { data = [] } = useQuery<IDealSecurityHistoryRow[]>(
    ["deal-securities-history", dealId],
    () => getDealSecuritiesHistoryAction(dealId!),
    { enabled: !!dealId }
  );

  const handleDeleteRow = useCallback(
    (row: IDealSecurityHistoryRow) => {
      const leaves = row.children.length > 0 ? row.children : [row];
      const ids = leaves
        .flatMap((l) =>
          l.measure_ids ? [l.measure_ids.units, l.measure_ids.price, l.measure_ids.total] : []
        )
        .filter((e): e is NonNullable<typeof e> => e !== null)
        .map((e) => e.data_id);

      if (!ids.length) return;

      const investorCount = row.children.length;
      const message =
        investorCount > 0
          ? `This will delete ${ids.length} measure entries across ${investorCount} investors for ${row.date}. This action cannot be undone.`
          : `This will delete up to ${ids.length} measure${ids.length !== 1 ? "s" : ""} for ${row.date}. This action cannot be undone.`;

      deleteModal({
        element: "securities entry",
        message,
        action: async () => {
          await Promise.all(ids.map((id) => deleteMeasureDataAction(id)));
          queryClient.invalidateQueries(["deal-securities-history", dealId]);
        }
      });
    },
    [deleteModal, deleteMeasureDataAction, dealId, queryClient]
  );

  const handleEditRow = useCallback(
    (row: IDealSecurityHistoryRow) => {
      if (!dealId || !row.batch_id) return;

      // Resolve leaves: for a parent use its children, otherwise the row itself
      const leaves = row.children.length > 0 ? row.children : [row];
      if (leaves.some((l) => !l.measure_ids)) return;

      const measureData: MeasureData[] = [];
      const groups: Group[] = [];
      let keyId = 1;
      let groupId = 1;

      // One group per measure slot (units / price / total) — all investors inside the same group
      for (const slot of ["units", "price", "total"] as const) {
        const slotEntries = leaves
          .map((l) => ({ entry: l.measure_ids![slot], leaf: l }))
          .filter((x): x is { entry: NonNullable<typeof x.entry>; leaf: typeof x.leaf } => x.entry !== null);

        if (!slotEntries.length) continue;

        const slotMeasureId = slotEntries[0].entry.measure_id;

        for (const { entry, leaf } of slotEntries) {
          const bankParts = [leaf.institution_name, leaf.account_number].filter(Boolean);
          const bank_detail_name = bankParts.length ? bankParts.join(" - ") : null;

          measureData.push({
            investor_group: null,
            id: entry.data_id,
            key: keyId++,
            group_id: groupId,
            value: entry.amount.toString(),
            currency: currency,
            date: entry.date,
            description: null,
            investor_id: leaf.investor_id,
            investor_name: leaf.investor_name,
            bank_detail_id: leaf.bank_detail_id,
            bank_detail_name,
            round_id: leaf.round_id,
            accounting_date: null,
            enable_split: false,
            effective_range_from: null,
            effective_range_to: null,
            files: null
          });
        }

        groups.push({
          id: groupId++,
          deal_id: dealId,
          sub_deal_id: null,
          measure_id: slotMeasureId,
          total_amount: slotEntries.reduce((sum, x) => sum + x.entry.amount, 0),
          original_measure_id: slotMeasureId,
          delete_measure_data_ids: [],
          original_group_measure_data_ids: slotEntries.map((x) => x.entry.data_id),
          files: null
        });
      }

      setEditState({ groups, measureData, batchId: row.batch_id });
    },
    [dealId, currency]
  );

  const handleSave = useCallback(
    async (data: { groups: MeasureDataGroup[]; roundsByDeals: Record<number, MeasureDataRound[]> }) => {
      await createManyMeasureDataAction({ ...data, edit_type: "batch" });
      setEditState(null);
      queryClient.invalidateQueries(["deal-securities-history", dealId]);
    },
    [createManyMeasureDataAction, dealId, queryClient]
  );

  const handleSaveAsDraft = useCallback(
    async (data: MeasureDataDraft["data"]) => {
      if (!dealId) return;
      await saveMeasureDataDraftAction({ deal_id: dealId, data });
      setEditState(null);
    },
    [dealId, saveMeasureDataDraftAction]
  );

  const handleEditClose = useCallback(() => {
    setEditState(null);
  }, []);

  if (!data.length) {
    return (
      <Typography variant="body2" sx={{ color: "#71A0B2", p: 2 }}>
        No securities data available.
      </Typography>
    );
  }

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ border: "1px solid #DBE8F5", borderRadius: "8px", boxShadow: "none", flexGrow: 1 }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ height: 40 }}>
              <HeaderCell>Date</HeaderCell>
              <HeaderCell>Round</HeaderCell>
              <HeaderCell>Type</HeaderCell>
              <HeaderCell>Investor</HeaderCell>
              <HeaderCell>Institution</HeaderCell>
              <HeaderCell>Account Number</HeaderCell>
              <HeaderCell>Branch Number</HeaderCell>
              <HeaderCell>Units</HeaderCell>
              <HeaderCell>Unit Price</HeaderCell>
              <HeaderCell>Total</HeaderCell>
              <HeaderCell sx={{ width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, idx) => (
              <SecuritiesHistoryRow key={idx} row={row} depth={0} onEdit={handleEditRow} onDelete={handleDeleteRow} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {editState && (
        <Drawer open handleClose={handleEditClose} title="Edit Row">
          <GroupsForm
            deal_id={dealId!}
            subDeals={subDeals}
            parentId={null}
            defaultCurrency={currency as any}
            initialGroups={editState.groups}
            initialMeasureData={editState.measureData}
            initialRoundsByDeal={rounds ?? {}}
            batch_id={editState.batchId}
            onSave={handleSave}
            onSaveAsDraft={handleSaveAsDraft}
            onClose={handleEditClose}
            disableDealSelect
            disableSubDealSelect
            disableAddGroup
            disableRemoveGroup
          />
        </Drawer>
      )}

    </>
  );
};
